import { useFrame } from '@react-three/fiber';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import {
  Group,
  Mesh,
  PerspectiveCamera,
  ShaderMaterial,
  Texture,
  Uniform,
  Vector3,
} from 'three';

import { QuadCloud } from '0_canvas/StaticCloud/QuadCloud';
import type { GroupProps } from 'types/r3f';

import {
  scaleBackground,
  WEATHER_SCREEN_PREFIX,
} from '../WeatherScreens.utils';
import fragmentShader from './shader.frag';
import vertexShader from './shader.vert';

interface CloudyScreenProps extends GroupProps {
  backgroundTexture: Texture;
}

const FlatCloudsBar = () => {
  const cloudsRef = useRef<Group>(null);

  useFrame(() => {
    if (!cloudsRef.current) return;

    [...Array(50).keys()].forEach((_, i) => {
      const cloudUp = cloudsRef.current?.getObjectByName(
        `cloudy_screen_cloud_up_${i}`
      );
      const cloudDown = cloudsRef.current?.getObjectByName(
        `cloudy_screen_cloud_down_${i}`
      );

      if (cloudUp)
        cloudUp.position.x =
          cloudUp.position.x < -125 ? 50 : cloudUp.position.x - 0.005;
      if (cloudDown)
        cloudDown.position.x =
          cloudDown.position.x > 125 ? -50 : cloudDown.position.x + 0.001;
    });
  });

  return (
    <>
      <group ref={cloudsRef}>
        {[...Array(25).keys()].map((_, i) => (
          <QuadCloud
            position={[50 - i * 7, 12, -400]}
            segmentCount={4}
            bounds={new Vector3(1, 1, 1)}
            scale={[1.3, 1.3, 1.3]}
            volume={2}
            opacity={0.3}
            key={`cloudy_${i}_down`}
            name={`cloudy_screen_cloud_down_${i}`}
            cloudStyle="white"
          />
        ))}
        {[...Array(50).keys()].map((_, i) => (
          <QuadCloud
            position={[50 - i * 3.5, 15, -400]}
            segmentCount={4}
            bounds={new Vector3(1, 1, 1)}
            scale={[1.5, 1.5, 1.5]}
            volume={2}
            opacity={0.3}
            key={`cloudy_${i}_up`}
            name={`cloudy_screen_cloud_up_${i}`}
            cloudStyle="white"
          />
        ))}
      </group>
    </>
  );
};

const CloudyScreen = forwardRef<Group, CloudyScreenProps>(
  ({ backgroundTexture, ...props }, ref) => {
    const backgroundRef = useRef<Mesh>(null!);
    const screenRef = useRef<Group>(null!);

    const materialRef = useRef<ShaderMaterial>(
      new ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms: {
          uBackground: new Uniform(backgroundTexture),
        },
      })
    );

    useFrame(({ camera, size }) => {
      if (!backgroundRef.current || !backgroundTexture) return;

      scaleBackground({
        mesh: backgroundRef.current,
        camera: camera as PerspectiveCamera,
        size,
        texture: backgroundTexture,
        positionZ: screenRef.current.position.z,
      });

      materialRef.current.uniforms.uBackground = new Uniform(backgroundTexture);
    });

    useImperativeHandle(ref, () => screenRef.current);

    return (
      <group name={WEATHER_SCREEN_PREFIX + 'cloudy'} ref={screenRef} {...props}>
        <FlatCloudsBar />
        <mesh name="background" ref={backgroundRef} rotation={[Math.PI, 0, 0]}>
          <planeGeometry args={[1, 1]} />
          <primitive object={materialRef.current} attach="material" />
        </mesh>
      </group>
    );
  }
);

CloudyScreen.displayName = 'CloudyScreen';
export default CloudyScreen;
